/**
 * HTTP — the only way a component talks to the API. One client per controller connection:
 * `this.http` (busy element = `this.element`).
 *
 * A request never rejects. It resolves to exactly one of:
 *   { ok: true,  status, data }          success (data is null for 204/205)
 *   { ok: false, error: ApiError }       failure of any kind
 *   { ok: false, stale: true }           superseded or disposed: the caller just returns
 *
 * @typedef {import('./lifetime.js').Lifetime} Lifetime
 * @typedef {{ ok: true, status: number, data: any } | { ok: false, error: ApiError, stale?: false } | { ok: false, stale: true }} Result
 * @typedef {{
 *   query?: Record<string, unknown>, format?: string, body?: unknown,
 *   lane?: string, timeout?: number, signal?: AbortSignal, as?: 'json' | 'text' | 'blob', accept?: string,
 * }} RequestOptions
 */
import { emitApp } from './events.js'

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])
const STALE = Object.freeze({ ok: false, stale: true })
const NON_FIELD = new Set(['non_field_errors', 'detail', '__all__'])
const MAX_MESSAGE = 1000

/**
 * The one error type. `message` and `fieldErrors` come from the server: render as text only.
 * kind: url | network | timeout | session | auth-check | forbidden | not-found | validation | conflict | server | parse
 * (`config` is produced by the base for invalid component configuration, never by a request).
 */
export class ApiError extends Error {
  /**
   * @param {string} kind
   * @param {{ status?: number, message?: string, fieldErrors?: Record<string, string[]> }} [info]
   */
  constructor(kind, { status = 0, message = '', fieldErrors = Object.create(null) } = {}) {
    super(message)
    this.name = 'ApiError'
    this.kind = kind
    this.status = status
    this.fieldErrors = fieldErrors
  }
}

/**
 * The CSRF token for unsafe requests. Production sets CSRF_COOKIE_HTTPONLY, so the cookie is invisible to JS:
 * the page renders the token into `<meta name="csrf-token">` (base.html, staff/base.html) and that is read first;
 * the cookie is only a fallback for pages without the meta tag.
 * @param {string} [name]
 */
export function csrfToken(name = 'csrftoken') {
  const meta = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
  if (meta) return meta
  const prefix = `${name}=`
  const cookie = document.cookie.split('; ').find((part) => part.startsWith(prefix))
  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : ''
}

/**
 * Build a same-origin URL under `/api/`, or null. Cross-origin, non-http(s), empty and non-API URLs
 * are refused, so the session cookie and the CSRF token can never be sent elsewhere.
 * @param {unknown} url
 * @param {Record<string, unknown>} [query] null/undefined/'' are skipped, arrays repeat the key
 * @param {string} [format] value for ?format=
 * @returns {string | null}
 */
export function apiUrl(url, query = {}, format = '') {
  if (typeof url !== 'string' || url.trim() === '') return null
  let target
  try {
    target = new URL(url, document.baseURI)
  } catch {
    return null
  }
  if (target.origin !== window.location.origin) return null
  if (!target.pathname.startsWith('/api/')) return null
  for (const [key, value] of Object.entries(query)) {
    target.searchParams.delete(key)
    for (const item of [value].flat()) {
      if (item !== null && item !== undefined && item !== '') target.searchParams.append(key, String(item))
    }
  }
  if (format) target.searchParams.set('format', format)
  return target.href
}

/**
 * Turn an error body into an ApiError. Understands the envelope
 * `{error: {detail, status_code}}` and, for not-yet-migrated endpoints, bare DRF bodies.
 * `detail` string -> message; object -> fieldErrors (dotted paths for nested serializers).
 * @param {unknown} body
 * @param {number} status
 */
export function parseError(body, status) {
  const envelope = isObject(body) && isObject(body.error) ? body.error : null
  const detail = envelope ? envelope.detail : isObject(body) ? (body.detail ?? body) : null
  /** @type {string[]} */
  const messages = []
  /** @type {Record<string, string[]>} */
  const fieldErrors = Object.create(null)
  collect(detail, '', messages, fieldErrors)
  const kind =
    status === 401 ? 'session'
    : status === 403 ? 'forbidden'
    : status === 404 ? 'not-found'
    : status === 409 ? 'conflict'
    : status === 400 || status === 422 ? 'validation'
    : 'server' // 5xx, 429 and any other status: the status number stays on the error
  return new ApiError(kind, { status, message: messages.join(' '), fieldErrors })
}

function collect(value, path, messages, fieldErrors) {
  if (value === null || value === undefined) return
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      if (isObject(item) || Array.isArray(item)) collect(item, path ? `${path}.${index}` : String(index), messages, fieldErrors)
      else push(item)
    })
  } else if (isObject(value)) {
    for (const [key, item] of Object.entries(value)) {
      collect(item, NON_FIELD.has(key) ? path : path ? `${path}.${key}` : key, messages, fieldErrors)
    }
  } else push(value)

  function push(item) {
    const text = String(item).slice(0, MAX_MESSAGE)
    if (path) (fieldErrors[path] ??= []).push(text)
    else messages.push(text)
  }
}

/**
 * Build a same-origin http(s) URL for TEXT mode (server-rendered fragments, same-origin stylesheets), or null.
 * Unlike `apiUrl` it does not require `/api/`; origin is still checked, so the session cookie never leaves the site.
 * @param {unknown} url
 * @param {Record<string, unknown>} [query]
 * @returns {string | null}
 */
export function textUrl(url, query = {}) {
  if (typeof url !== 'string' || url.trim() === '') return null
  let target
  try {
    target = new URL(url, document.baseURI)
  } catch {
    return null
  }
  if (target.origin !== window.location.origin) return null
  if (target.protocol !== 'http:' && target.protocol !== 'https:') return null
  for (const [key, value] of Object.entries(query)) {
    target.searchParams.delete(key)
    for (const item of [value].flat()) {
      if (item !== null && item !== undefined && item !== '') target.searchParams.append(key, String(item))
    }
  }
  return target.href
}

/** The file name of a Content-Disposition value: RFC 5987 `filename*` first, then `filename`; '' when none (never a path). */
export function filenameOf(disposition) {
  const star = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(disposition)
  let name = ''
  if (star) {
    try {
      name = decodeURIComponent(star[2].trim())
    } catch {
      name = ''
    }
  }
  if (!name) {
    const plain = /filename\s*=\s*(?:"((?:[^"\\]|\\.)*)"|([^;]+))/i.exec(disposition)
    name = plain ? (plain[1] ?? plain[2]).trim().replace(/\\(.)/g, '$1') : ''
  }
  return name.split(/[\\/]/).pop().replace(/[\u0000-\u001f]/g, '').trim()
}

const isObject = (value) => typeof value === 'object' && value !== null && !Array.isArray(value)
const isJson = (response) => /\bjson\b/i.test(response.headers.get('content-type') ?? '')

/**
 * @param {Lifetime} lifetime requests are aborted and answered `stale` when it is disposed
 * @param {{ busy?: Element | null, timeout?: number }} [options]
 *   busy: element that gets aria-busy while requests are in flight; timeout: default per-request ms
 */
export function createHttp(lifetime, { busy = null, timeout: defaultTimeout = 15000 } = {}) {
  /** @type {Map<string, object>} latest request per lane */
  const latest = new Map()
  let inFlight = 0

  const setBusy = (delta) => {
    inFlight = Math.max(0, inFlight + delta)
    if (!busy) return
    if (inFlight > 0) busy.setAttribute('aria-busy', 'true')
    else busy.removeAttribute('aria-busy')
  }
  lifetime.add(() => setBusy(-inFlight))

  /**
   * @param {string} method
   * @param {string} url
   * @param {RequestOptions} [options]
   * @returns {Promise<Result>}
   */
  async function request(method, url, { query, format, body, lane, timeout = defaultTimeout, signal, as = 'json', accept } = {}) {
    method = method.toUpperCase()
    const blob = as === 'blob' // BLOB mode: `data` = { blob, filename, contentType } (a file download; a JSON answer such as 202 {job_id} comes back parsed); same errors, lanes, abort, CSRF
    const text = as === 'text' || blob // TEXT mode: the body comes back as a string (HTML fragment, stylesheet); same errors, lanes, abort, CSRF
    const target = text ? textUrl(url, query) : apiUrl(url, query, format)
    if (!target) return { ok: false, error: new ApiError('url') }
    if (!lifetime.active || signal?.aborted) return STALE

    const abort = new AbortController()
    let timedOut = false
    const timer = setTimeout(() => {
      timedOut = true
      abort.abort()
    }, timeout)
    const forget = lifetime.add(() => {
      clearTimeout(timer)
      abort.abort()
    })
    const onExternalAbort = () => abort.abort()
    signal?.addEventListener('abort', onExternalAbort, { once: true })
    const entry = {}
    if (lane) {
      latest.get(lane)?.abort()
      latest.set(lane, Object.assign(entry, { abort: () => abort.abort() }))
    }
    const stale = () => !lifetime.active || signal?.aborted || (lane !== undefined && latest.get(lane) !== entry)

    const headers = { Accept: accept ?? (text ? 'text/html, text/css;q=0.9, */*;q=0.5' : 'application/json') }
    if (UNSAFE.has(method)) headers['X-CSRFToken'] = csrfToken()
    let payload
    if (typeof FormData === 'function' && body instanceof FormData) {
      payload = body // multipart (the file-input upload): the browser sets the Content-Type with its boundary
    } else if (body !== undefined && body !== null) {
      payload = JSON.stringify(body)
      headers['Content-Type'] = 'application/json'
    }

    setBusy(+1)
    try {
      const response = await fetch(target, {
        method,
        headers,
        body: payload,
        signal: abort.signal,
        credentials: 'same-origin',
        cache: 'no-store',
        redirect: 'manual', // an API never redirects: any redirect is a signal, a write never follows one
      })
      if (stale()) return STALE
      if (response.type === 'opaqueredirect' || (response.status >= 300 && response.status < 400)) {
        return { ok: false, error: new ApiError('auth-check', { status: response.status }) }
      }
      const empty = response.status === 204 || response.status === 205
      // a JSON error body is parsed in every mode (the server's message, field errors); a successful text body is not JSON
      const jsonError = text && (!response.ok || blob) && isJson(response) // blob mode: a JSON answer (e.g. 202 {job_id}) is data, not a file
      const data = empty ? undefined
        : jsonError ? await response.json().catch(() => undefined)
        : blob ? await response.blob().catch(() => undefined)
        : text ? await response.text().catch(() => undefined)
        : !isJson(response) ? undefined
        : await response.json().catch(() => undefined)
      if (stale()) return STALE
      if (timedOut) return { ok: false, error: new ApiError('timeout') } // the 15 s include body parsing
      const errorBody = !text || jsonError ? data : undefined // an HTML error page is never parsed into messages
      if (response.status === 401) {
        emitApp('session-expired') // the shell offers re-login; no navigation, dirty forms survive
        return { ok: false, error: new ApiError('session', { status: 401, message: parseError(errorBody, 401).message }) }
      }
      if (!response.ok) return { ok: false, error: parseError(errorBody, response.status) }
      if (!empty && data === undefined) return { ok: false, error: new ApiError('parse', { status: response.status }) }
      if (blob && data instanceof Blob) {
        const disposition = response.headers.get('content-disposition') ?? ''
        return { ok: true, status: response.status, data: { blob: data, filename: filenameOf(disposition), contentType: response.headers.get('content-type') ?? '' } }
      }
      return { ok: true, status: response.status, data: data ?? null }
    } catch {
      if (stale()) return STALE
      if (timedOut) return { ok: false, error: new ApiError('timeout') }
      if (abort.signal.aborted) return STALE
      return { ok: false, error: new ApiError('network') }
    } finally {
      clearTimeout(timer)
      forget()
      signal?.removeEventListener('abort', onExternalAbort)
      if (lane !== undefined && latest.get(lane) === entry) latest.delete(lane)
      setBusy(-1)
    }
  }

  return {
    request,
    /** @param {string} url @param {RequestOptions} [options] */
    get: (url, options) => request('GET', url, options),
    /** @param {string} url @param {RequestOptions} [options] */
    post: (url, options) => request('POST', url, options),
    /** @param {string} url @param {RequestOptions} [options] */
    put: (url, options) => request('PUT', url, options),
    /** @param {string} url @param {RequestOptions} [options] */
    patch: (url, options) => request('PATCH', url, options),
    /** @param {string} url @param {RequestOptions} [options] */
    delete: (url, options) => request('DELETE', url, options),
    /** TEXT mode GET: `data` is the response body as a string; same-origin any path (not only /api/). @param {string} url @param {RequestOptions} [options] */
    text: (url, options) => request('GET', url, { ...options, as: 'text' }),
    /** BLOB mode GET: `data` = { blob, filename, contentType } (filename from Content-Disposition, '' when absent). @param {string} url @param {RequestOptions} [options] */
    blob: (url, options) => request('GET', url, { ...options, as: 'blob' }),
  }
}
